import React from 'react'
import * as propTypes from 'prop-types'
import { isEqual, capitalize, isEmpty, cloneDeep } from 'lodash'
import AddIcon from '@material-ui/icons/Add'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import FilterListIcon from '@material-ui/icons/FilterList'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import TableCell from '@material-ui/core/TableCell/TableCell'
import { Grid } from '@mui/material'
import withHOCs from 'util/withHocs'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import FlexibleTable from '../../components/EnhancedTable/FlexibleTable'
import {
  getProjects,
  downloadAllProjectSamples,
  setTabvalue,
  saveProjectSearchValue,
  setFavouriteProjects,
  fetchProjectStatus,
} from '../../store/projects/actionCreator'
import { updateShootType } from '../../store/newShootType/actionCreator'
import { setHeaderTitleArray } from '../../store/layout/actionCreator'
import {
  createShootType,
  getShootType,
} from '../../store/newShootType/actionCreator'
import { ShootTypes } from '../../enums/shootTypes'
import { projectTabLabels, defaultProjectsTab } from '../../enums/Tabs'
import CustomTabs from '../../components/Tabs/Tabs'
import { selectPageFilters } from '../../store/filters/selectors'
import { setFilters } from '../../store/filters/actionCreator'
import { IconButton } from '@material-ui/core'
import ProjectMetricsView from './ProjectMetricsView'
import apiConfig from 'apiConfig'

const styles = (theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3),
  },
  pageHeader: {
    width: '100%',
    height: '40px',
    position: 'relative',
  },
  pageHeaderButton: {
    display: 'inline',
    position: 'absolute',
    top: 0,
    right: 0,
  },
  add: {
    marginRight: '10px',
  },
  paper: {
    width: '100%',
    marginTop: theme.spacing(4),
    display: 'grid',
    overflowX: 'auto',
  },
  defaultButton: {
    maxWidth: '250px',
    width: 'auto',
    margin: theme.spacing(1),
  },
})

class ProjectsPage extends React.Component {
  constructor(props) {
    super(props)
    const { filters: propsFilter = {}, tabValue = '' } = this.props
    this.state = {
      tooltipIsOpen: false,
      currentTabValue: tabValue,
      filters: propsFilter,
      metricsHasMore: true,
      metricsInitialized: false,
    }
    this.metricsScrollPending = false
    this.metricsContainerRef = React.createRef()
    this.shouldStickToBottom = false
  }

  componentDidMount() {
    const {
      setHeaderTitleArray,
      setFilters = () => {},
      setTabvalue = () => {},
    } = this.props
    setHeaderTitleArray([{ title: 'Projects', link: '/projects' }])
    setFilters({}, 'projects')
    setTabvalue(defaultProjectsTab)
  }

  saveFilters() {
    const { setFilters, setTabvalue = () => {} } = this.props
    const { filters = {}, currentTabValue = '' } = this.state
    const filterCopy = Object.assign({}, filters)
    if (!isEmpty(filterCopy)) {
      setFilters(filterCopy, 'projects')
    }
    setTabvalue(currentTabValue)
  }

  componentDidUpdate(prevProps, prevState) {
    const { currentTabValue = '', filters = {}, metricsHasMore } = this.state
    const {
      getProjects = () => {},
    } = this.props
    const {
      currentTabValue: prevCurrentTabValue = '',
      filters: prevFilters = {},
    } = prevState
    if (prevProps.getProjectsPending && !this.props.getProjectsPending) {
      this.metricsScrollPending = false
      if (currentTabValue === 'METRICS') {
        const { projects = [], fetchProjectStatus, projectsTotalCount = 0 } = this.props
        // Use the page that was actually just fetched (prevFilters), not the next queued page
        const loadedPage = prevFilters.page || 1
        const rowsPerPage = prevFilters.page_size || 5
        const totalPages = rowsPerPage ? Math.ceil(projectsTotalCount / rowsPerPage) : 0
        const nextHasMore = projects.length < projectsTotalCount &&
          (totalPages ? loadedPage < totalPages : projects.length >= rowsPerPage)
        if (metricsHasMore !== nextHasMore) {
          this.setState({ metricsHasMore: nextHasMore })
        }
        // Fetch status for each newly loaded project
        projects.forEach((project) => {
          const projectId = project.id || project.project_id
          if (projectId) {
            fetchProjectStatus(String(projectId))
          }
        })
        if (this.shouldStickToBottom) {
          this.scrollMetricsToBottom()
          this.shouldStickToBottom = false
        }
      }
    }

    const isMetricsTab = currentTabValue === 'METRICS'
    const didEnterMetricsTab = isMetricsTab && prevCurrentTabValue !== 'METRICS'
    const didPageChange = (filters.page || 1) !== (prevFilters.page || 1)
    
    if (
      !isEqual(currentTabValue, prevCurrentTabValue) ||
      !isEqual(filters, prevFilters)
    ) {
      if (isMetricsTab) {
        if (
          (!this.state.metricsInitialized && didEnterMetricsTab) ||
          didPageChange
        ) {
          if (!this.state.metricsInitialized) {
            this.setState({ metricsInitialized: true })
          }
          getProjects(filters)
        }
      } else {
        getProjects(filters)
      }
    }

    if (currentTabValue !== 'METRICS') {
      this.metricsScrollPending = false
    }

    if (filters.page === 1 && prevFilters.page !== 1 && !metricsHasMore) {
      this.setState({ metricsHasMore: true })
    }
  }

  componentWillUnmount() {
    const locationHref = window.location.href
    if (locationHref.indexOf('projects') === -1) {
      this.saveSearchValue('')
    }
  }

  handleTooltipOpen = () => {
    this.setState({ tooltipIsOpen: !this.state.tooltipIsOpen })
    this.autoCloseTimer = setTimeout(this.handleTooltipClose.bind(this), 10000)
  }

  handleTooltipClose = () => {
    this.setState({ tooltipIsOpen: false })
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer)
    }
  }

  handleMetricsScroll = (event) => {
    const { getProjectsPending = false, projectsTotalCount = 0, projects = [] } = this.props
    const { currentTabValue = '', filters = {}, metricsHasMore } = this.state
    if (
      currentTabValue !== 'METRICS' ||
      getProjectsPending ||
      !metricsHasMore ||
      this.metricsScrollPending
    ) {
      return
    }

    const target = event.currentTarget
    const scrolledToBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 10

    if (!scrolledToBottom) return

    // If we've already loaded all projects, stop
    if (projectsTotalCount > 0 && projects.length >= projectsTotalCount) return

    const rowsPerPage = filters.page_size || 5
    const totalPages = rowsPerPage
      ? Math.ceil(projectsTotalCount / rowsPerPage)
      : 0
    const currentPage = filters.page || 1
    if (totalPages && currentPage >= totalPages) {
      this.setState({ metricsHasMore: false })
      return
    }

    this.metricsScrollPending = true
    this.shouldStickToBottom = true

    const nextFilters = {
      ...filters,
      page: currentPage + 1,
      page_size: rowsPerPage,
    }

    this.setState({ filters: nextFilters })
  }

  scrollMetricsToBottom = () => {
    const container = this.metricsContainerRef.current
    if (!container) return
    window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
  }

  handleNewShootType() {
    this.props.navigate(`/projects/create`)
  }

  handleGetShootType(e = {}) {
    this.saveFilters()
    const { id: projectID = '' } = e
    this.props.navigate(`/projects/${projectID}/view`)
  }

  setCurrentTabValue = (currentTab = '') => {
    const { filters: stateFilters = {} } = this.state
    const { filters: propsFilters = {} } = this.props
    if (!propsFilters['created_by']) {
      delete stateFilters['created_by']
    }
    const nextFilters = Object.assign({}, stateFilters)
    if (currentTab === 'METRICS') {
      nextFilters['status'] = 'PENDING'
      // delete nextFilters['status']
      nextFilters['page'] = 1
      nextFilters['page_size'] = 5
    } else {
      nextFilters['page'] = 1
      nextFilters['page_size'] = 1000
      nextFilters['status'] = currentTab
    }
    this.setState({
      currentTabValue: currentTab,
      filters: nextFilters,
      metricsHasMore:
        currentTab === 'METRICS' ? true : this.state.metricsHasMore,
    })
  }

  filterClick = () => {
    const {
      auth: {
        session: {
          userInfo: { lanId = '' },
        },
      },
    } = this.props

    const { filters: stateFilters = {} } = this.state
    const filtersCopy = Object.assign({}, stateFilters)
    const currentFilterExists =
      Object.keys(filtersCopy).indexOf('created_by') !== -1
    if (currentFilterExists) {
      delete filtersCopy['created_by']
      this.setFilters(Object.assign({}, filtersCopy))
    } else {
      this.setFilters(Object.assign({}, stateFilters, { created_by: lanId }))
    }
  }

  setFilters = (filters = {}) => {
    this.setState({
      filters: filters,
    })
  }

  additionalFiltersContent = () => {
    const { classes = {} } = this.props
    const { filters: stateFilters = {} } = this.state
    const currentFilterExists =
      Object.keys(stateFilters).indexOf('created_by') !== -1

    return (
      <Tooltip title={'My Projects'}>
        <Button
          data-cy="btnMyProjects"
          color="secondary"
          className={classes.filterButton}
          variant={currentFilterExists ? 'contained' : 'outlined'}
          onClick={() => {
            this.filterClick()
          }}
          startIcon={<FilterListIcon />}
        >
          {'My Projects'}
        </Button>
      </Tooltip>
    )
  }

  saveSearchValue = (searchValue = '') => {
    const { saveProjectSearchValue = () => {} } = this.props
    saveProjectSearchValue(searchValue)
  }
  getFormattedData = (projectsDetails = []) => {
    return projectsDetails.reduce((acc, option) => {
      option['isAnAdditionalInfoRow'] = true
      acc.push(option)
      return acc
    }, [])
  }
  callBack = () => {
    const { getProjects = () => {} } = this.props
    getProjects(this.state.filters)
  }
  onShootTypeUpdateFunction = (
    shootTypeId,
    row = {},
    shootType,
    callBack = () => {}
  ) => {
    const { onShootTypeUpdate = () => {}, shipToLocations = [] } = this.props
    const {
      name = '',
      channel = '',
      locationName = '',
      shootStartDate = '',
      shootEndDate = '',
      marketingPid = '',
      samplesRequiredBy = '',
      notes = '',
      auto_order_enabled = false,
      ship_to_phone_number = '',
      project_editors = [],
      isFavourite = false,
    } = row

    const updateRequest = {
      name: name,
      channel: channel,
      locationId: shipToLocations.filter((x) => x.building === locationName),
      shootStartDate: shootStartDate,
      shootEndDate: shootEndDate,
      marketingPid: marketingPid,
      samplesRequiredBy,
      notes: notes,
      auto_order_enabled: auto_order_enabled,
      ship_to_phone_number: ship_to_phone_number,
      project_editors: project_editors?.map((obj) => obj.lan_id) || [],
      is_favourite: !isFavourite,
    }

    onShootTypeUpdate(shootTypeId, updateRequest, shootType, callBack, true)
  }
  renderAdditionalContent = (
    row = {},
    uniqueKey = '',
    classes = {},
    errorRowTooltipMessage = ''
  ) => {
    const {
      isAnAdditionalInfoRow = false,
      isFavourite = false,
      hasPicklist = false,
      id = '',
    } = row
    const { onShootTypeUpdate = () => {} } = this.props
    return (
      <TableCell key={`additional-${uniqueKey}`} style={{ width: '130px' }}>
        {isAnAdditionalInfoRow ? (
          <Tooltip title={errorRowTooltipMessage}>
            <Grid container direction="row">
              <Grid item>
                <IconButton
                  onClick={() =>
                    this.onShootTypeUpdateFunction(
                      id,
                      row,
                      ShootTypes.PROJECT,
                      this.callBack
                    )
                  }
                >
                  {isFavourite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
              </Grid>
              {hasPicklist && (
                <Grid item>
                  <IconButton style={{ pointerEvents: 'none' }}>
                    <ShoppingCartIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Tooltip>
        ) : (
          ''
        )}
      </TableCell>
    )
  }
  renderTabsContent = () => {
    const {
      columnData,
      projects,
      getProjectsPending = false,
      searchValue = '',
      projectMetricsById = {},
      projectStatusById = {},
      dispatch,
    } = this.props
    const { currentTabValue = '' } = this.state
    const currentTabValueObj =
      projectTabLabels.find((obj) => obj.value === currentTabValue) || {}
    const { label: currentTabLabel = '' } = currentTabValueObj
    const tableLabel = `${capitalize(currentTabLabel) + ' Projects'}`
    const projectData = this.getFormattedData(cloneDeep(projects))
    if (currentTabValue === 'METRICS') {
      return (
        <ProjectMetricsView
          projectDetailsLoading={getProjectsPending}
          projects={projects}
          projectDetailsMap={projectMetricsById}
          projectStatusById={projectStatusById}
          classes={this.props.classes}
          onMetricsScroll={this.handleMetricsScroll}
          metricsContainerRef={this.metricsContainerRef}
        />
      )
    }
    return (
      <FlexibleTable
        cascadeFieldKeys={[]}
        checkBoxEnabled={false}
        columnData={columnData}
        data={projectData}
        uniqueIdentifierInData={'id'}
        dataFieldKeys={[]}
        editEnabled={false}
        enableSearch
        additionalIconInfoContent={this.renderAdditionalContent}
        // enableDownload={true}
        // downloadFunction={() => downloadAllProjectSamples(projects)}
        showPopup={(e) => this.handleGetShootType(e)}
        tableTitle={tableLabel}
        disableTablePagination={true}
        disableItemCount={true}
        clickableRowButton="OPEN"
        additionalActions={true}
        additionalFiltersContent={this.additionalFiltersContent}
        isLoading={getProjectsPending}
        saveFilterValue={this.saveSearchValue}
        savedSearchValue={searchValue}
        enableMultiSearch
        enableScrolls
      />
    )
  }

  render() {
    const {
      classes = {},
      auth: { isAuthorized = () => {} },
    } = this.props

    const {
      roles: { helpvendor: helpvendorRoles = [] },
    } = apiConfig

    return (
      <div className={classes.root}>
        <div className={classes.pageHeader}>
          <Typography variant="h4" gutterBottom data-cy="projectsTitle">
            Projects
          </Typography>
          <div className={classes.pageHeaderButton}>
            <Button
              id="createNewProject"
              data-id={ShootTypes.PROJECT}
              data-cy="newProjectBtn"
              color="primary"
              className={classes.defaultButton}
              type="button"
              variant="contained"
              onClick={this.handleNewShootType.bind(this)}
              startIcon={<AddIcon />}
            >
              New Project
            </Button>
          </div>
        </div>
        <Paper className={classes.paper} id={'projectsTab'}>
          <CustomTabs
            tabLabels={
              !isAuthorized(helpvendorRoles)
                ? projectTabLabels
                : projectTabLabels.filter((obj) => obj.value !== 'METRICS')
            }
            renderTabsContent={this.renderTabsContent}
            setCurrentTabValue={this.setCurrentTabValue}
            defaultTab={this.props.tabValue || defaultProjectsTab}
            showTabsPanel={false}
          />
        </Paper>
      </div>
    )
  }
}

function mapStateToProps(state) {
  const {
    projects: {
      columnData = [],
      projects = [],
      projectMetricsById = {},
      getProjectsPending = false,
      projectsTotalCount = 0,
      tabValue = '',
      filterValue = '',
      projectStatusById = {},
    },
    newShootType: {
      goNextUpdatePageFlag = false,
      shootType = '',
      goNextViewPageFlag = false,
      page = 1,
      page_size = 50,
    },
    newShipment: { shipToLocations = [] },
  } = state
  return {
    columnData,
    goNextUpdatePageFlag,
    shootType,
    projects,
    projectMetricsById,
    projectStatusById,
    goNextViewPageFlag,
    page,
    page_size,
    getProjectsPending,
    projectsTotalCount,
    filters: selectPageFilters('projects')(state),
    tabValue,
    searchValue: filterValue,
    shipToLocations,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    getProjects: (params = {}) => {
      dispatch(getProjects(params))
    },
    downloadAllProjectSamples: (projects) => {
      dispatch(downloadAllProjectSamples(projects))
    },
    setHeaderTitleArray: (headerTitleArray) =>
      dispatch(setHeaderTitleArray(headerTitleArray)),
    createShootType: (userId, shootType) =>
      dispatch(createShootType(userId, shootType)),
    getShootType: (row, shootType, page, rowsPerPage) =>
      dispatch(getShootType(row, shootType, page, rowsPerPage)),
    setFilters: (filters, pageName) =>
      dispatch(setFilters(filters, 'projects')),
    setTabvalue: (tabValue) => dispatch(setTabvalue(tabValue)),
    saveProjectSearchValue: (searchValue) =>
      dispatch(saveProjectSearchValue(searchValue)),
    favouriteProjects: (id, callBack, isFavourite) =>
      dispatch(setFavouriteProjects(id, callBack, isFavourite)),
    fetchProjectStatus: (projectId) =>
      dispatch(fetchProjectStatus(projectId)),
    onShootTypeUpdate: (
      shootTypeId,
      updateRequest,
      shootType,
      callback = () => {},
      isFavouriteUpdate = false
    ) =>
      dispatch(
        updateShootType(
          shootTypeId,
          updateRequest,
          shootType,
          callback,
          isFavouriteUpdate
        )
      ),
  }
}

ProjectsPage.propTypes = {
  columnData: propTypes.array,
  projects: propTypes.array,
  navigate: propTypes.func,
}

export default withHOCs({
  auth: true,
  nav: true,
  styles,
  connectParams: {
    mapStateToProps,
    mapDispatchToProps,
  },
})(ProjectsPage)
